export type Group = {
    id: string
    name: string | null
    items: any[]
    isSection: boolean
    hideLineItems: boolean
    displaySubtotal: boolean
    fromBundleId: string | null
    subTotal: number
}

export const sectionGroupKey = (sectionIdKey: string, uniqueIdKey: string) =>
    (item: any, index: number): string =>
        item[sectionIdKey] ? item[sectionIdKey]
            : item[uniqueIdKey] ? 'line-' + item[uniqueIdKey]
                : 'line-idx-' + index

export const getGroupedItems = (
    orderedData: any[],
    sectionNameKey: string,
    sectionIdKey: string,
    useInclusiveTotal: boolean = false,
    groupKeyOf: (item: any, index: number) => string = (c) => c.groupId
): Group[] => {
    const grouped = new Map<string, any[]>()
    orderedData.forEach((item, index) => {
        const key = groupKeyOf(item, index)
        const existing = grouped.get(key)
        if (existing) existing.push(item)
        else grouped.set(key, [item])
    })
    return Array.from(grouped.entries())
        .sort(([, [a]], [, [b]]) => a.LineNumber > b.LineNumber ? 1 : -1)
        .map(([key, x]) => ({
            id: key,
            name: x[0][sectionNameKey] || null,
            items: x,
            isSection: !!x[0][sectionIdKey],
            hideLineItems: x[0]?.HideLineItems || false,
            displaySubtotal: x[0]?.DisplaySubtotal || false,
            fromBundleId: x[0]?.FromBundleID || null,
            subTotal: x.filter((y: any) => !isNaN(+y.LineTotalExclusive))
                .map((y: any) => useInclusiveTotal ? (y.LineTotalExclusive * (1 + y.TaxPercentage / 100)) : y.LineTotalExclusive)
                .reduce((p: any, c: any) => p + c, 0)
        }))
}

const groupsEqual = (a: Group, b: Group) =>
    a.items.length === b.items.length
    && a.items.every((item, i) => item === b.items[i])
    && a.name === b.name
    && a.isSection === b.isSection
    && a.hideLineItems === b.hideLineItems
    && a.displaySubtotal === b.displaySubtotal
    && a.fromBundleId === b.fromBundleId
    && a.subTotal === b.subTotal

export const shareUnchangedGroups = (prev: Group[], next: Group[]): Group[] => {
    const prevById = new Map(prev.map(g => [g.id, g]))
    const out = next.map(g => {
        const old = prevById.get(g.id)
        return old && groupsEqual(old, g) ? old : g
    })
    return out.length === prev.length && out.every((g, i) => g === prev[i]) ? prev : out
}
